/**
 * Cart Images Enhancement
 *
 * Replaces generic cart thumbnails with occasion-specific images
 * from content-map.json when available.
 *
 * Progressive enhancement:
 * - Liquid renders the correct generic fallback image immediately
 * - This script upgrades thumbnails to occasion-specific images if the
 *   collection has custom product_slides defined in the content map
 * - If the occasion image fails to load (404), the original generic
 *   image is preserved — no broken images.
 *
 * Targets elements with [data-cart-item-image][data-occasion][data-virtual-handle].
 * Uses a debounced MutationObserver to re-enhance after cart DOM updates.
 */
(function () {
  'use strict';

  // Content map cache (shared with ProductImages if available)
  var mapCache = null;

  /**
   * Get the content-map.json URL from the inline <script> tag in theme.liquid.
   * The element id is "content-map-data" and its textContent is a JSON string
   * containing the Shopify CDN URL.
   * @returns {string|null}
   */
  function getContentMapUrl() {
    // Primary: #content-map-data (rendered by theme.liquid)
    var el = document.getElementById('content-map-data');
    if (el) {
      try { return JSON.parse(el.textContent.trim()); } catch (e) { /* fall through */ }
    }
    // Legacy fallback: #content-map-url (if it ever exists separately)
    var legacy = document.getElementById('content-map-url');
    if (legacy) {
      try { return JSON.parse(legacy.textContent.trim()); } catch (e) { /* fall through */ }
    }
    return null;
  }

  /**
   * Load content-map.json, reusing ProductImages cache when possible.
   * @returns {Promise<Object|null>}
   */
  async function loadContentMap() {
    if (mapCache) return mapCache;

    // Try ProductImages module first (may already have it cached)
    if (window.ProductImages) {
      try {
        mapCache = await window.ProductImages.loadMediaMap();
        return mapCache;
      } catch (e) { /* fall through */ }
    }

    // Fall back to fetching directly from the inline URL
    var url = getContentMapUrl();
    if (!url) return null;
    try {
      var res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      mapCache = await res.json();
      return mapCache;
    } catch (e) {
      return null;
    }
  }

  /**
   * Get the assets base URL from the content-map URL.
   * @returns {string}
   */
  function getAssetsBase() {
    var url = getContentMapUrl();
    if (!url) return '';
    return url.split('content-map.json')[0] || '';
  }

  /**
   * Get the assets version from the content-map URL's ?v= parameter.
   * @returns {string}
   */
  function getAssetsVersion() {
    var url = getContentMapUrl();
    if (!url) return '';
    var parts = url.split('?v=');
    return parts.length > 1 ? parts[1] : '';
  }

  /**
   * Preload an image and resolve with the URL if it loads, or null on error.
   * @param {string} url - Image URL to test
   * @returns {Promise<string|null>}
   */
  function preloadImage(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () { resolve(url); };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  var isEnhancing = false;

  /**
   * Main enhancement function.
   * Finds all cart item images that have an occasion, looks up the
   * occasion-specific first image from the content map, verifies it
   * loads successfully, then swaps it in.
   */
  async function enhanceCartImages() {
    if (isEnhancing) return;
    isEnhancing = true;

    try {
      var images = document.querySelectorAll('[data-cart-item-image]');
      if (images.length === 0) return;

      // Only load the map if at least one item has an occasion
      var hasOccasion = false;
      for (var i = 0; i < images.length; i++) {
        if (images[i].dataset.occasion && images[i].dataset.occasion.length > 0) {
          hasOccasion = true;
          break;
        }
      }
      if (!hasOccasion) return;

      var map = await loadContentMap();
      if (!map) return;

      var assetsBase = getAssetsBase();
      var assetsVersion = getAssetsVersion();

      // Process each image — preload occasion image before swapping
      var promises = [];

      for (var j = 0; j < images.length; j++) {
        (function (img) {
          var virtualHandle = img.dataset.virtualHandle;
          var occasion = img.dataset.occasion;
          if (!virtualHandle || !occasion) return;

          // Skip if already enhanced (avoid duplicate work)
          if (img.dataset.cartImageEnhanced === occasion) return;

          // Skip occasion-specific images when flagged for generic fallback
          if (map.collections && map.collections[occasion] && map.collections[occasion].use_generic_images) return;

          // Look up occasion-specific slides for this product
          var occasionSlides =
            map.collections &&
            map.collections[occasion] &&
            map.collections[occasion].product_slides &&
            map.collections[occasion].product_slides[virtualHandle];

          if (!occasionSlides || occasionSlides.length === 0) return;

          // Get the first image filename (filter out video objects)
          var filename = null;
          for (var k = 0; k < occasionSlides.length; k++) {
            if (typeof occasionSlides[k] === 'string') { filename = occasionSlides[k]; break; }
          }
          if (!filename) return;
          var imageUrl = '';
          if (/^https?:\/\//i.test(filename) || filename.charAt(0) === '/') {
            imageUrl = filename;
          } else {
            imageUrl = assetsBase + filename;
            if (assetsVersion) imageUrl += '?v=' + assetsVersion;
          }

          // Apply Shopify CDN optimizations (width + webp)
          var separator = imageUrl.indexOf('?') !== -1 ? '&' : '?';
          var optimizedUrl = imageUrl + separator + 'width=300&format=webp';

          // Preload the image — only swap if it actually loads
          var p = preloadImage(optimizedUrl).then(function (loadedUrl) {
            if (loadedUrl) {
              img.src = loadedUrl;
              img.dataset.cartImageEnhanced = occasion;
            }
            // If loadedUrl is null (404/error), keep the original generic image
          });

          promises.push(p);
        })(images[j]);
      }

      // Wait for all preloads to complete
      if (promises.length > 0) {
        await Promise.all(promises);
      }
    } catch (e) {
      console.warn('Cart image enhancement failed:', e);
    } finally {
      isEnhancing = false;
    }
  }

  // --- Initialization ---

  // Run on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceCartImages);
  } else {
    enhanceCartImages();
  }

  // Watch for cart DOM changes (after add-to-cart, quantity updates, etc.)
  // Uses a debounced MutationObserver on the cart drawer and main cart containers.
  var debounceTimer = null;

  var observer = new MutationObserver(function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(enhanceCartImages, 150);
  });

  function startObserving() {
    var targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('main-cart-items'),
    ].filter(Boolean);

    targets.forEach(function (target) {
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  // --- Upload Thumbnails in Cart ---

  /**
   * Show small thumbnail previews of customer-uploaded photos in the cart.
   * Reads the Shopify cart JSON to find _photo_* properties on line items,
   * then renders a thumbnail strip below each cart item that has uploads.
   */
  async function enhanceCartUploadThumbs() {
    try {
      var res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      var cart = await res.json();
      if (!cart.items || cart.items.length === 0) return;

      // Build a map of line item key => photo URLs
      var itemPhotos = {};
      cart.items.forEach(function (item) {
        if (!item.properties) return;
        var photos = [];
        for (var key in item.properties) {
          if (key.match(/^_photo_\d+$/) && item.properties[key]) {
            photos.push(item.properties[key]);
          }
        }
        if (photos.length > 0) {
          itemPhotos[item.key] = photos;
        }
      });

      if (Object.keys(itemPhotos).length === 0) return;

      // Find cart item rows in the DOM and inject thumbnails
      var cartItems = document.querySelectorAll('[id^="CartDrawer-Item-"], [id^="CartItem-"]');
      cartItems.forEach(function (row) {
        // Extract the line item key from the row's data or index
        var keyAttr = row.dataset.cartItemKey || row.dataset.key;

        // Try to match by index if no key attribute
        if (!keyAttr) {
          var indexMatch = row.id.match(/(\d+)$/);
          if (indexMatch) {
            var idx = parseInt(indexMatch[1], 10) - 1;
            if (idx >= 0 && idx < cart.items.length) {
              keyAttr = cart.items[idx].key;
            }
          }
        }

        if (!keyAttr || !itemPhotos[keyAttr]) return;

        // Skip if already enhanced
        if (row.querySelector('.cart-upload-thumbs')) return;

        var photos = itemPhotos[keyAttr];
        var detailsCell = row.querySelector('.cart-item__details');
        if (!detailsCell) return;

        var thumbsContainer = document.createElement('div');
        thumbsContainer.className = 'cart-upload-thumbs';

        var maxShow = 6;
        var showCount = Math.min(photos.length, maxShow);

        for (var i = 0; i < showCount; i++) {
          var img = document.createElement('img');
          img.className = 'cart-upload-thumbs__img';
          img.src = photos[i] + '-/scale_crop/72x72/center/';
          img.alt = 'Uploaded photo ' + (i + 1);
          img.loading = 'lazy';
          img.width = 36;
          img.height = 36;
          thumbsContainer.appendChild(img);
        }

        if (photos.length > maxShow) {
          var more = document.createElement('span');
          more.className = 'cart-upload-thumbs__more';
          more.textContent = '+' + (photos.length - maxShow);
          thumbsContainer.appendChild(more);
        }

        detailsCell.appendChild(thumbsContainer);
      });
    } catch (e) {
      // Silently fail — not critical
    }
  }

  // Run upload thumb enhancement after the main cart image enhancement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(enhanceCartUploadThumbs, 300);
    });
  } else {
    setTimeout(enhanceCartUploadThumbs, 300);
  }

  // Also re-run on cart DOM changes (piggyback on existing observer debounce)
  var uploadThumbTimer = null;
  var uploadObserver = new MutationObserver(function () {
    clearTimeout(uploadThumbTimer);
    uploadThumbTimer = setTimeout(enhanceCartUploadThumbs, 400);
  });

  function startUploadThumbObserver() {
    var targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('main-cart-items'),
    ].filter(Boolean);

    targets.forEach(function (target) {
      uploadObserver.observe(target, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startUploadThumbObserver);
  } else {
    startUploadThumbObserver();
  }

  // Expose for manual invocation and debugging
  window.CartImages = {
    enhance: enhanceCartImages,
    enhanceUploadThumbs: enhanceCartUploadThumbs,
    debug: async function () {
      var map = await loadContentMap();
      var base = getAssetsBase();
      var imgs = document.querySelectorAll('[data-cart-item-image]');
      console.log('CartImages debug:');
      console.log('  Content map loaded:', !!map);
      console.log('  Assets base:', base);
      console.log('  Cart item images found:', imgs.length);
      for (var i = 0; i < imgs.length; i++) {
        var img = imgs[i];
        console.log('  Item ' + i + ':', {
          virtualHandle: img.dataset.virtualHandle,
          occasion: img.dataset.occasion,
          enhanced: img.dataset.cartImageEnhanced || 'no',
          currentSrc: img.src,
        });
        if (img.dataset.occasion && map) {
          var useGeneric = map.collections && map.collections[img.dataset.occasion] && map.collections[img.dataset.occasion].use_generic_images;
          var oi = !useGeneric && map.collections &&
            map.collections[img.dataset.occasion] &&
            map.collections[img.dataset.occasion].product_slides &&
            map.collections[img.dataset.occasion].product_slides[img.dataset.virtualHandle];
          console.log('    Occasion slides in map:', oi || 'none');
          if (oi && oi.length > 0) {
            var firstImg = null;
            for (var fi = 0; fi < oi.length; fi++) { if (typeof oi[fi] === 'string') { firstImg = oi[fi]; break; } }
            if (firstImg) console.log('    Would load:', base + firstImg);
          }
        }
      }
    },
  };
})();
